import { SerializedError } from '@reduxjs/toolkit';
import { Draft, produce } from 'immer';
import { QueryError } from 'lib-shared-auth';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import { AsyncStatus } from 'lib-shared-types-api';
import type {
  ConversationEvent,
  ReadConversationApiArg,
  ReadConversationApiResponse,
} from 'lib-shared-types-api';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { conversationsApi } from 'apis/conversations';
import { userContentApi } from 'apis/userContent';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

type ReadConversation = (arg: ReadConversationApiArg) => void;

type UseReadPatchedConversationResult = [
  readConversation: ReadConversation,
  result: {
    /** The patched conversation data. */
    readonly data?: ReadConversationApiResponse;
    /** The error from reading the unpatched conversation. */
    readonly error?: QueryError | SerializedError;
    /** The async status. */
    readonly asyncStatus?: AsyncStatus;
  },
];

/**
 * A hook for reading conversation while also patching it, for example by
 * fetching and patching email events with their respective original email
 * content.
 */
export const useReadPatchedConversation =
  (): UseReadPatchedConversationResult => {
    const dispatch = useAppDispatch();
    const [patchedResponse, setPatchedResponse] =
      useState<ReadConversationApiResponse>();
    const [patchAsyncStatus, setPatchAsyncStatus] =
      useState<AsyncStatus>('uninitialized');

    const [
      readUnpatchedConversation,
      {
        data: unpatchedConversationData,
        error,
        isFetching,
        isError,
        isUninitialized,
      },
    ] = conversationsApi.useLazyReadConversationQuery();

    /** Initiates patching the read conversation response. */
    const patchResponse = useCallback(async () => {
      if (!unpatchedConversationData) {
        return;
      }

      setPatchAsyncStatus('pending');

      const patchedEvents = await Promise.all<ConversationEvent>(
        unpatchedConversationData.data.history.main_conversation.events.map(
          (event) =>
            new Promise((resolve) => {
              // Handles patching inbound email message added events by fetching
              // their original email content from another endpoint and
              // rewriting the event text field.
              if (
                event.type === 'conversation:message_added' &&
                (event.channel === 'contact_form' ||
                  event.channel === 'email') &&
                event.direction === 'inbound' &&
                event.email_original_content_url
              ) {
                dispatch(
                  userContentApi.endpoints.readOriginalEmail.initiate({
                    path: event.email_original_content_url,
                  }),
                )
                  .then((response) => {
                    resolve(
                      produce(event, (draft) => {
                        draft.text =
                          response.data?.data.msg.rewritten_html ?? draft.text;
                      }),
                    );
                  })
                  .catch((error) => {
                    // If for some reason the endpoint errors out, we fallback
                    // to the original event because we have already it, and
                    // log to Sentry for further investigation.
                    resolve(event);

                    Sentry.captureException(error, {
                      tags: {
                        team: SentryTeam.Conversations,
                      },
                    });
                  });
              } else {
                resolve(event);
              }
            }),
        ),
      );

      setPatchAsyncStatus('fulfilled');
      setPatchedResponse(
        produce(unpatchedConversationData, (draft) => {
          draft.data.history.main_conversation.events = patchedEvents as Draft<
            typeof patchedEvents
          >;
        }),
      );
    }, [dispatch, unpatchedConversationData]);

    /**
     * Represents the cumulative async status of reading and patching the
     * conversation.
     */
    const asyncStatus = useMemo<AsyncStatus>(() => {
      if (isUninitialized || patchAsyncStatus === 'uninitialized') {
        return 'uninitialized';
      }

      if (isFetching || patchAsyncStatus === 'pending') {
        return 'pending';
      }

      if (isError) {
        return 'rejected';
      }

      return 'fulfilled';
    }, [isError, isFetching, isUninitialized, patchAsyncStatus]);

    /** Invokes reading the conversation. */
    const readConversation = useCallback<ReadConversation>(
      (arg) => {
        readUnpatchedConversation(arg);
      },
      [readUnpatchedConversation],
    );

    // Handles patching the main conversation.
    useEffect(() => {
      if (unpatchedConversationData) {
        patchResponse();
      }
    }, [unpatchedConversationData, patchResponse]);

    return [
      readConversation,
      {
        data: patchedResponse,
        error,
        asyncStatus,
      },
    ];
  };
